import moment from 'moment';
// Ekstensi .js dipertahanin biar trailData.check.mjs tetep jalan di node polos.
import { kmhToKnot } from '../units.js';
import { formatDateTime } from '../date.js';

// Respons /summary/trail: array-of-array + header `fields`, jauh lebih kecil
// dari blok data /summary/aggregated/period. Bentuk keluarannya disamain sama
// mapGpsSeriesToTrail biar polyline & popup gak perlu tau sumbernya.
export function parseTrailResponse(body) {
  const fields = body?.fields || [];
  const rows = body?.trail || [];
  const at = (name) => fields.indexOf(name);
  const iTs = at('timestamp');
  const iLat = at('latitude');
  const iLng = at('longitude');
  const iSpeed = at('speed');
  const iTrack = at('track');
  const iWaveHeight = at('waveHeight');
  const iWavePeriod = at('wavePeriod');
  const iSeaState = at('seaState');

  if (iLat < 0 || iLng < 0 || !rows.length) {
    return { coordinates: [], coordinatesData: [] };
  }

  const pick = (row, idx) => (idx < 0 ? undefined : row[idx]);
  const points = rows
    .filter((row) => row[iLat] != null && row[iLng] != null)
    .map((row) => {
      const timestamp = pick(row, iTs);
      return {
        latLong: [row[iLat], row[iLng]],
        heading: pick(row, iTrack),
        timestamp,
        gpsData: {
          timestamp,
          datetime: timestamp ? moment(timestamp * 1000).format('YYYY-MM-DD HH:mm:ss') : undefined,
          speed: pick(row, iSpeed),
          track: pick(row, iTrack),
          // gelombang cuma ikut kalau endpoint ngirim fieldnya
          ...(iWaveHeight < 0 ? {} : { waveHeight: row[iWaveHeight] }),
          ...(iWavePeriod < 0 ? {} : { wavePeriod: row[iWavePeriod] }),
          ...(iSeaState < 0 ? {} : { seaState: row[iSeaState] }),
        },
      };
    });

  return { coordinates: points.map((p) => p.latLong), coordinatesData: points };
}

// Jam yang memuat `timestamp` — dipakai buat narik detail satu titik dot trail.
export function hourWindow(timestamp) {
  const start = Math.floor(timestamp / 3600) * 3600;
  return { start, end: start + 3600 };
}

// Rentang jam yang diambil buat satu titik, buat ditampilin di popup dot trail.
// Offset cuma ditempel sekali di ujung.
export function hourWindowLabel(timestamp) {
  if (!Number.isFinite(Number(timestamp))) return null;
  const { start, end } = hourWindow(Number(timestamp));
  const from = formatDateTime(start * 1000, 'DD MMM YYYY HH:mm', null, false);
  return `${from} - ${formatDateTime(end * 1000, 'HH:mm')}`;
}

// Halaman berfilter ngasih start/end eksplisit; Home cuma ngasih `days`.
export function trailRangeFrom({
  start, end, days = 1, lastTimestamp,
} = {}) {
  if (start != null && end != null) return { start, end };
  if (!lastTimestamp) return null;
  return {
    start: moment(hourWindow(lastTimestamp).start * 1000).subtract(days, 'day').unix(),
    end: lastTimestamp,
  };
}

// Satu titik dot trail = satu rentang sejam. Endpoint rollup ngasih ringkasan
// jam itu; ini map-in ke bentuk yang dibaca popup dot trail.
export function rollupPointDetail(summaryData) {
  const gps = summaryData?.gps?.summary || {};
  const rpmAvg = (summaryData?.rpm?.summary || {}).average || {};
  const aeSecs = (summaryData?.ae?.summary || {}).runningSeconds || {};
  // Flowrate diambil per channel, sama kayak fms-summary.js:263 — bukan
  // portAverageFlow di akar summary, yang itungannya beda (volume / flowTime).
  const fm = summaryData?.flowmeter?.summary || {};
  const activeAe = Object.keys(aeSecs).find((k) => Number(aeSecs[k]) > 0);
  const aeNumber = activeAe ? Number(activeAe.replace(/[^0-9]/g, '')) : undefined;

  return {
    totalCruise: gps.totalDistance,
    // averageSpeed rollup satuannya km/h, sama kayak endpoint lama.
    speedKnots: gps.averageSpeed == null ? undefined : kmhToKnot(gps.averageSpeed),
    locationText: gps.lastPosition?.zone?.name,
    currentAe: Number.isFinite(aeNumber) ? aeNumber : undefined,
    portRpm: rpmAvg.PORT,
    starboardRpm: rpmAvg.STARBOARD,
    centerRpm: rpmAvg.CENTER,
    portInFlowrate: fm.PORT_IN?._averageFlowrate,
    starboardInFlowrate: fm.STARBOARD_IN?._averageFlowrate,
  };
}

// Buat role GPS Viewer: trail cuma dari endpoint GPS-only (/series/aggregated/gps/period,
// yang sama dipake halaman Device Detail), bukan /summary/aggregated/period yang bundle
// semua devcType (butuh permission RPM/AE/flowmeter yang gak dipunya role ini). Shape
// response-nya beda dari processSummarySeriesData (array `series` per titik, bukan dict
// per-devcType), jadi dipetain terpisah - field non-GPS (RPM/AE/fuel) sengaja kosong.
export const mapGpsSeriesToTrail = (series) => {
  const points = series.map((point) => ({
    latLong: [point.latitude, point.longitude],
    heading: point.track,
    timestamp: point.timestamp,
    gpsData: {
      timestamp: point.timestamp, datetime: point.datetime, speed: point.speed, track: point.track,
    },
  }));
  return {
    coordinates: points.map((p) => p.latLong),
    coordinatesData: points,
  };
};
